import React, { useEffect, useMemo } from "react";

// Material Styles
import { IconButton } from "../IconButton/IconButton";
import { Icon, Grid } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
// Styling
import { Theme, useTheme, styled } from "@mui/material";

import classNames from "classnames";
// form components
import { SelectField } from "src/components/FormikComponents/SelectField";

interface ITablePaginationProps {
  mosaicId: string;
  count: number;
  page: number;
  rowsPerPage: number;
  className?: string;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, newPage: number, rowsPerPage: number) => any;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  parentElement?: HTMLElement;
  lastSearched?: Date;
}

const OCTablePagination = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  margin: 12,
  "& .rowsperpage_icon": { margin: "12px 6px 12px 0px" },
  "& .pagination_selection": { margin: "12px 0px", width: 263 },
  "& .pagination_controls": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  "& .pagination_status": {
    userSelect: "none",
    flexGrow: 1,
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
    margin: "0px 10px",
    height: 48,
    lineHeight: "48px",
    fontSize: 12,
    fontWeight: 300,
  },
}));

/**
 * This is the pagination for the table taken from a material ui example
 * @param props -
 */
export const TablePagination = (props: ITablePaginationProps): JSX.Element => {
  const theme: Theme = useTheme();
  const { mosaicId, count, page, onPageChange, rowsPerPage, className, parentElement, lastSearched } = props;
  const rowsPerPageSelection = [
    // { value: "auto", label: "Auto" },
    { value: 5, label: "Results displayed per page: 5" },
    { value: 10, label: "Results displayed per page: 10" },
    { value: 25, label: "Results displayed per page: 25" },
    { value: 100, label: "Results displayed per page: 100" },
    { value: 250, label: "Results displayed per page: 250" },
  ];

  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);

  // Will hide pagination if there is no reason to show it.
  const displayPagination: boolean = React.useMemo(() => {
    return count > rowsPerPage;
  }, [count, rowsPerPage]);

  const returnRowsPerPage = (): any => {
    return rowsPerPageSelection.find((row: any) => row.value === rowsPerPage);
  };

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    onPageChange(event, 0, rowsPerPage);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    onPageChange(event, page - 1, rowsPerPage);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    onPageChange(event, page + 1, rowsPerPage);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1), rowsPerPage);
  };

  // If first page item ever exceeds item count, recalculate page count and set correct page.
  //! This issue was a result of user going to last page and setting rows to page to higher value.
  useEffect(() => {
    const pageItemsStart = page * rowsPerPage + 1;
    if (pageItemsStart > count) {
      const newMaxPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
      onPageChange(null, newMaxPage, rowsPerPage);
    }
  }, [page, count, rowsPerPage, onPageChange]);

  /**
   * onChange function for displaying the user's selected number of rows from the drop down menu
   * @param event -
   */
  const handleRowsPerPageDisplay = (event: any): void => {
    props.onRowsPerPageChange(event.value);
  };

  /**
   * Checks if the user provided an input for how many rows to display in the table
   *  Otherwise, it calculates the value of the last page
   */
  const pageItemsStart = page * rowsPerPage + 1;
  let pageItemsEnd = page * rowsPerPage + rowsPerPage;
  pageItemsEnd = pageItemsEnd > count ? count : pageItemsEnd;

  let lastPage = 0;
  if (rowsPerPage === null) {
    lastPage = 1;
  } else {
    lastPage = Math.max(0, Math.ceil(count / rowsPerPage));
  }

  return (
    <OCTablePagination className={classNames(className)}>
      <Grid container wrap="nowrap" style={{ width: "auto" }}>
        <Icon className="rowsperpage_icon">view_list</Icon>
        {/* NOTE: If user changes rows per page forcing the pagination menu
        to the bottom of the window, setting the values label as a key in a 
        wrapper below will force the Select field to remount and recalculate
        menu placement and height - CC*/}
        <Grid item key={returnRowsPerPage()?.label}>
          <SelectField
            className="pagination_selection"
            label=""
            name="rowsPerPage"
            value={returnRowsPerPage()}
            onChange={handleRowsPerPageDisplay}
            options={rowsPerPageSelection}
            menuPortalTarget={mosaicPane}
            menuPosition="fixed"
            maxMenuHeight={400}
            placeholder="Select results displayed per page"
          />
        </Grid>
      </Grid>

      {displayPagination ? (
        <div className="pagination_controls">
          <IconButton
            id="first_page"
            onClick={handleFirstPageButtonClick}
            disabled={page === 0}
            aria-label="First Page"
            size="large"
          >
            {!!theme && theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
          </IconButton>
          <IconButton
            id="previous_page"
            onClick={handleBackButtonClick}
            disabled={page === 0}
            aria-label="Previous Page"
            size="large"
          >
            {!!theme && theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
          </IconButton>
          <IconButton
            id="next_page"
            onClick={handleNextButtonClick}
            disabled={page + 1 >= lastPage}
            aria-label="Next Page"
            size="large"
          >
            {!!theme && theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
          </IconButton>
          <IconButton
            id="last_page"
            onClick={handleLastPageButtonClick}
            disabled={page + 1 >= lastPage}
            aria-label="Last Page"
            size="large"
          >
            {!!theme && theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
          </IconButton>
          <div className="pagination_status">
            Displaying results <span>{pageItemsStart}</span> to {pageItemsEnd} of <span>{count}</span>
          </div>
        </div>
      ) : (
        <div className="pagination_status">
          Displaying <span>{count}</span> results
        </div>
      )}

      {lastSearched && <div className="pagination_status">Last Search: {lastSearched.toISOString()}</div>}
    </OCTablePagination>
  );
};
